// src/pages/Privacy.js

import React from "react";
import { Helmet } from "react-helmet";
import "../styles/Privacy.css";

const Privacy = () => {
  return (
    <div className="privacy-page page">
      {/* SEO Metadata */}
      <Helmet>
        <title>Privacy Policy - GhrianGhraffy</title>
        <meta
          name="description"
          content="Privacy Policy for GhrianGhraffy, explaining how we collect, use, share, retain, and protect personal data for schools, staff, parents, our website, and our mobile app."
        />
      </Helmet>

      <div className="privacy-card">
        <header className="privacy-header">
          <p className="privacy-kicker">Privacy</p>
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-meta">Last updated: May 28, 2026</p>
        </header>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Introduction</h2>
          <p>
            Welcome to GhrianGhraffy, an app and web service for School
            Principals, School Staff, and authorised Parents to capture,
            upload, review, publish, and view school photos and videos securely.
            GhrianGhraffy is operated from Ireland. You can contact us about
            privacy at{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>
            .
          </p>
          <p>
            This Privacy Policy explains how we collect, use, share, retain, and
            protect information when you use our services, including:
          </p>
          <ul>
            <li>Our public website and registration pages;</li>
            <li>Our web dashboard for Principals and authorised Staff;</li>
            <li>Our mobile app;</li>
            <li>
              School website integrations used to show approved photos or
              videos where a school chooses to enable them; and
            </li>
            <li>
              Optional publishing integrations, such as posting selected
              approved content to a school&apos;s official Instagram account.
            </li>
          </ul>
          <p>
            We design GhrianGhraffy so schools can keep control of school media,
            reduce long-term storage of school images on personal devices, and
            manage who can access school photos and videos.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Who Controls the Data?</h2>
          <p>
            GhrianGhraffy handles different types of information in different
            roles:
          </p>
          <ul>
            <li>
              For service account information, such as login details, contact
              email addresses, security records, billing references, and service
              administration, GhrianGhraffy generally acts as a{" "}
              <strong>data controller</strong>.
            </li>
            <li>
              For pupil photos, videos, captions, class labels, and related
              school content uploaded by schools or authorised school users,
              GhrianGhraffy generally acts as a <strong>data processor</strong>{" "}
              for the school. The school is normally the data controller for
              that content.
            </li>
          </ul>
          <p>
            If you are a parent or guardian and have a concern about a pupil
            photo or school-controlled content, you should normally contact the
            school first. We will assist the school where needed.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Information We Collect</h2>
          <p>We collect and process the following categories of information:</p>
          <ul>
            <li>
              <strong>Account and Profile Information</strong>: Email address,
              display name, role (for example Principal, Staff, Parent, or
              Admin), school association, account status, and password data. We
              store passwords only in hashed form, not as plain text. We also
              use authentication tokens so users can stay signed in.
            </li>
            <li>
              <strong>School Setup and Administration Information</strong>:
              School name, roll number, school website, school search tag,
              class scheme, school year settings, staff invite status, parent
              invite status, publishing defaults, website integration status,
              Instagram connection status, and network-sharing settings.
            </li>
            <li>
              <strong>Photos, Videos, and Profile Photos</strong>: Photos and
              videos captured or uploaded by authorised users, profile photos or
              selfies used for account verification, thumbnails, file metadata,
              media type, dimensions, duration, and upload status. Parents may
              be asked to upload a profile photo before viewing school media so
              schools can verify accounts.
            </li>
            <li>
              <strong>Stories, Captions, Hashtags, Class Labels, Likes, and
              Comments</strong>: Text and labels added to posts or media items,
              including stories, titles, hashtags, class tags, approval status,
              likes, and comments.
            </li>
            <li>
              <strong>Drafts and Local Working Copies</strong>: When a user is
              capturing or preparing media for upload, the app may temporarily
              store working copies, drafts, or upload queue data on the device.
              The app is designed to remove working copies after successful
              upload, deletion, or cancellation where possible. Photos or
              videos already saved in a device&apos;s own camera roll remain
              under that device user&apos;s control.
            </li>
            <li>
              <strong>Device, Security, and Usage Information</strong>: Basic
              technical information needed to keep the service secure and
              working, such as login events, request logs, IP addresses,
              browser or device information, network status, error details,
              and security tokens.
            </li>
            <li>
              <strong>Billing and Subscription Information</strong>: For
              schools or Principals using paid features, we process limited
              billing references such as subscription status and Stripe customer
              or subscription identifiers. Payment card details are handled by
              Stripe and are not stored on our servers.
            </li>
            <li>
              <strong>Communications</strong>: Emails and service messages used
              for registration, approval, staff invites, parent invites,
              password reset, secure staff setup links, subscription notices,
              and support or privacy requests.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Mobile App Permissions</h2>
          <p>
            The GhrianGhraffy mobile app may request device permissions so that
            users can complete specific actions:
          </p>
          <ul>
            <li>
              <strong>Camera</strong>: Used to take school photos and videos,
              and to take profile photos where required for account
              verification.
            </li>
            <li>
              <strong>Microphone</strong>: Used only when recording videos that
              include audio.
            </li>
            <li>
              <strong>Photo Library or Gallery</strong>: Used when a user
              chooses existing photos or videos from their device for upload.
            </li>
            <li>
              <strong>Local Storage</strong>: Used for temporary working copies,
              thumbnails, drafts, upload queues, and secure sign-in data.
            </li>
          </ul>
          <p>
            These permissions are used for user-initiated app features. We do
            not use camera, microphone, or gallery access to collect media in
            the background.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">
            Cookies and Similar Technologies
          </h2>
          <p>
            On the GhrianGhraffy website and web dashboard, we may use cookies,
            browser storage, and similar technologies to:
          </p>
          <ul>
            <li>Keep users signed in and secure their sessions.</li>
            <li>Remember service preferences and account state.</li>
            <li>Protect the service from abuse or unauthorised access.</li>
          </ul>
          <p>
            You can usually control cookies through your browser settings.
            Disabling certain cookies or browser storage may affect core
            functionality such as logging in or staying signed in.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">How We Use Information</h2>
          <p>We use information to:</p>
          <ul>
            <li>
              Create and manage accounts for Principals, Staff, Parents, and
              Admin users.
            </li>
            <li>
              Authenticate users and provide role-specific access to features.
            </li>
            <li>
              Let authorised Staff and Principals capture, upload, review,
              approve, reject, publish, organise, and remove school media.
            </li>
            <li>
              Let authorised Parents view approved school media that the school
              has made available to them.
            </li>
            <li>
              Support school setup, staff invitations, parent invitations,
              website integrations, Instagram publishing, and school-level
              publishing defaults.
            </li>
            <li>
              Provide subscription, billing, trial, and upload-access features.
            </li>
            <li>
              Send service communications, security messages, registration
              emails, password-reset emails, approval emails, and staff or
              parent invitation emails.
            </li>
            <li>
              Keep the service reliable, secure, and protected against misuse.
            </li>
            <li>
              Comply with legal obligations and enforce our terms and policies.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Data Sharing</h2>
          <p>We do not sell personal information. We may share information:</p>
          <ul>
            <li>
              <strong>Within the school community</strong>, as part of normal
              service use. For example, Staff may upload photos for Principal
              review, Principals may approve content, and authorised Parents may
              view approved content for their school or class.
            </li>
            <li>
              <strong>With service providers</strong> that help us operate the
              service, including Vultr for hosting/infrastructure, GoDaddy for
              email or domain-related services, Stripe for payments, Expo/EAS
              for app build or distribution workflows, and Google Play for
              Android app distribution.
            </li>
            <li>
              <strong>With integrations chosen by the school</strong>, such as
              Meta/Instagram for optional posting to a school&apos;s official
              Instagram account, or school website/WordPress-style integrations
              for displaying approved school content on the school&apos;s own
              website.
            </li>
            <li>
              <strong>With legal or regulatory authorities</strong> where we are
              required to do so, or where necessary to protect users, schools,
              our rights, or the security of the service.
            </li>
          </ul>
          <p>
            We require service providers to handle information securely and only
            for the purposes of providing their services to us or to the school.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Third-Party Services</h2>
          <p>
            GhrianGhraffy currently uses or may interact with the following
            third-party services:
          </p>
          <ul>
            <li>Vultr for cloud hosting and infrastructure.</li>
            <li>GoDaddy for email or domain-related services.</li>
            <li>Stripe for subscription payments and billing workflows.</li>
            <li>
              Meta/Instagram where a school chooses to connect an official
              Instagram account for optional publishing.
            </li>
            <li>
              Expo/EAS and Google Play for building, testing, distributing, and
              delivering the mobile app.
            </li>
            <li>
              School website platforms, including WordPress-style integrations,
              where a school chooses to show approved content on its website.
            </li>
          </ul>
          <p>
            We do not currently use third-party analytics or crash-reporting
            tools in the app. If this changes, we will update this policy.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Data Storage and Security</h2>
          <ul>
            <li>
              <strong>On Your Device</strong>: Photos, videos, drafts,
              thumbnails, and upload queues may be stored temporarily while the
              app prepares or retries an upload. Authentication tokens and basic
              profile details may be stored locally using browser storage or
              secure device storage so users can stay signed in.
            </li>
            <li>
              <strong>On Our Servers</strong>: Uploaded media, profile data,
              account data, school setup data, and service logs are stored on
              our servers and served over HTTPS through{" "}
              <a href="https://ghrianghraffy.com">
                https://ghrianghraffy.com
              </a>{" "}
              and related API endpoints.
            </li>
            <li>
              <strong>Access Controls</strong>: Access to school media is
              controlled by user role, school membership, review status,
              publishing settings, and signed media links. Parent access may be
              restricted until a required profile photo is uploaded.
            </li>
            <li>
              <strong>Security Measures</strong>: We use reasonable technical
              and organisational measures to protect information against
              unauthorised access, loss, misuse, or alteration. No system can be
              guaranteed 100% secure, so users should keep login details
              confidential and tell us or their school if they suspect
              unauthorised access.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Retention</h2>
          <p>
            We keep personal data only for as long as necessary to provide the
            service, fulfil the purposes described in this policy, comply with
            legal obligations, resolve disputes, and enforce agreements.
          </p>
          <ul>
            <li>
              Working copies and drafts on a device are intended to be temporary
              and are removed by the app where possible after upload,
              cancellation, or deletion.
            </li>
            <li>
              Uploaded school media and related data are retained while the
              school uses the service, unless the school requests deletion of
              specific content sooner.
            </li>
            <li>
              If a trial account does not convert to a subscription, we may
              delete the trial school&apos;s photos, related accounts, and
              school-specific configuration after the trial period ends.
            </li>
            <li>
              Billing, security, and service records may be retained for longer
              where needed for legal, accounting, fraud-prevention, or security
              purposes.
            </li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Account and Data Deletion</h2>
          <p>
            To request deletion of your GhrianGhraffy account or personal data,
            email{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>{" "}
            from the email address associated with your account. Please include
            the school name and the account email address so we can identify the
            relevant records.
          </p>
          <p>
            Some information is controlled by the school, especially pupil
            photos, videos, captions, class labels, and school publishing
            decisions. For school-controlled content, we may need to work with
            the school before deleting or changing the content. Parents and
            guardians should normally contact the school first for removal
            requests relating to pupil photos or school media.
          </p>
          <p>
            If a school has an active subscription, trial, unresolved billing
            state, or Stripe billing identifiers, deletion or transfer of a
            Principal account or school account may require additional steps
            before it can be completed. We will explain those steps when we
            respond to the request.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Your Rights</h2>
          <p>
            Depending on your location, including under GDPR for users in the
            European Economic Area, Ireland, and the United Kingdom, you may
            have rights to access, correct, delete, restrict, or object to
            certain processing of your personal data. You may also have the
            right to data portability and the right to lodge a complaint with a
            data protection authority.
          </p>
          <p>
            You can exercise these rights by contacting us at{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>
            . We may need to verify your identity before responding. For
            school-controlled photo or video content, we may ask you to contact
            your school, and we will work with the school where appropriate.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">GDPR and Legal Bases</h2>
          <p>
            We aim to process personal data in accordance with the General Data
            Protection Regulation (&quot;GDPR&quot;) and applicable data protection
            laws. We rely on one or more of the following legal bases:
          </p>
          <ul>
            <li>
              <strong>Contract</strong>: where processing is necessary to
              provide GhrianGhraffy to a user or school, including account
              access, uploads, review, publishing, and subscription features.
            </li>
            <li>
              <strong>Legitimate interests</strong>: where we or a school have
              a legitimate interest that is not overridden by rights and
              freedoms, such as securing the service, preventing misuse,
              improving reliability, or enabling a school to communicate school
              life with authorised families.
            </li>
            <li>
              <strong>Legal obligation</strong>: where we must process or
              retain data to comply with legal, tax, accounting, or regulatory
              requirements.
            </li>
            <li>
              <strong>Consent</strong>: where consent is used as the legal basis,
              such as where a school relies on parental consent for use of pupil
              images under its own policies. Consent for school-controlled
              pupil content is normally managed by the school.
            </li>
          </ul>
          <p>
            If we transfer personal data outside the EEA/UK, we will take
            appropriate steps to protect it, such as using Standard Contractual
            Clauses or other lawful transfer mechanisms where required.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Children&apos;s Privacy</h2>
          <p>
            GhrianGhraffy is intended for use by schools, authorised school
            staff, Principals, parents, and guardians. We do not offer accounts
            directly to children and children cannot sign themselves up for the
            service.
          </p>
          <p>
            The service may include photos or videos of children where a school
            or authorised staff member uploads them. Schools are responsible for
            ensuring they have an appropriate legal basis, consent process, or
            other lawful ground under applicable law and school policy before
            capturing, uploading, publishing, or sharing pupil images.
          </p>
          <p>
            GhrianGhraffy provides technical controls to help schools manage
            review, access, publication, removal, and visibility of school
            media. If you believe a child&apos;s information or image has been
            uploaded without proper authorisation, contact the school first or
            email{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>{" "}
            so we can assist.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time to reflect
            changes in our practices, technologies, service providers, or legal
            requirements. If we make significant changes, we will notify users
            through the app, website, or email where appropriate. The latest
            version will always be available on this page.
          </p>
        </section>

        <section className="privacy-section">
          <h2 className="privacy-section-title">Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or how we handle
            personal data, contact us at{" "}
            <a href="mailto:privacy@ghrianghraffy.com">
              privacy@ghrianghraffy.com
            </a>
            .
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
